import { Bell, Search, LogOut, User, Clock, CalendarCheck, MessagesSquare, QrCode, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useAuthStore } from '@/store/authStore';
import { useMyTodayAppointments } from '@/hooks/useMyTodayAppointments';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { isProbablyMobile } from '@/utils/isProbablyMobile';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/utils/api';
import {
  AttendanceActionPreset,
  AttendanceEventReason,
  confirmPresence,
  getActionPresets,
  getPresenceStatus,
  postAttendanceEvent,
} from '@/services/attendanceService';

function statusColor(status: string) {
  switch (status) {
    case 'COMPLETED':
      return 'bg-green-500';
    case 'CANCELLED':
    case 'NO_SHOW':
      return 'bg-gray-400';
    default:
      return 'bg-amber-500';
  }
}

function formatTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

type StaffConvRow = {
  id: string;
  title: string;
  unreadCount: number;
  lastMessage: { bodyPreview: string; createdAt: string } | null;
};

export default function Header() {
  const { logout, user } = useAuthStore();
  const attendanceEnabled = user?.requiresAttendance !== false;
  const isAdmin = user?.role === 'ADMIN';
  const showMobileScanner = isAdmin && isProbablyMobile() && attendanceEnabled;
  const navigate = useNavigate();
  const { appointments, activeCount, refetch } = useMyTodayAppointments();
  const [open, setOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<AttendanceActionPreset | null>(null);
  const [reason, setReason] = useState<AttendanceEventReason>('QR_UNAVAILABLE');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { data: staffConv = [] } = useQuery({
    queryKey: ['staff-messaging', 'conversations'],
    queryFn: async () => {
      const r = await apiClient.get('/staff-messaging/conversations');
      return r.data.data as StaffConvRow[];
    },
  });

  const { data: attendancePresets = [] } = useQuery({
    queryKey: ['attendance', 'presets'],
    queryFn: getActionPresets,
    enabled: quickOpen && attendanceEnabled,
  });

  const presenceStatus = useQuery({
    queryKey: ['attendance', 'presence-status'],
    queryFn: getPresenceStatus,
    refetchInterval: attendanceEnabled ? 60_000 : false,
    enabled: attendanceEnabled,
  });

  const staffUnread = useMemo(
    () => staffConv.reduce((s, c) => s + (c.unreadCount || 0), 0),
    [staffConv],
  );

  const staffPreview = useMemo(
    () => staffConv.filter((c) => c.unreadCount > 0).slice(0, 15),
    [staffConv],
  );

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logout effettuato con successo');
    } catch (error) {
      toast.error('Errore durante il logout');
    }
  };

  const handleConfirmPresence = async () => {
    try {
      await confirmPresence();
      toast.success('Presenza confermata');
      void presenceStatus.refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Conferma presenza non riuscita');
    }
  };

  const openQuickPreset = (preset: AttendanceActionPreset) => {
    setSelectedPreset(preset);
    setReason(preset.eventType === 'CHECK_IN' || preset.eventType === 'CHECK_OUT' ? 'QR_UNAVAILABLE' : 'FORGOT_TO_STAMP');
    setNote('');
  };

  const submitQuickStamp = async () => {
    if (!selectedPreset) return;
    const needsEmergencyNote = selectedPreset.eventType === 'CHECK_IN' || selectedPreset.eventType === 'CHECK_OUT' || reason === 'OTHER';
    if (needsEmergencyNote && note.trim().length < 5) {
      toast.error('Inserisci una nota di almeno 5 caratteri');
      return;
    }
    setSubmitting(true);
    try {
      await postAttendanceEvent({
        presetId: selectedPreset.id,
        source: 'PC_MANUAL',
        reason,
        note,
      });
      toast.success('Timbratura rapida registrata');
      setSelectedPreset(null);
      setQuickOpen(false);
      void presenceStatus.refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Timbratura rapida non registrata');
    } finally {
      setSubmitting(false);
    }
  };

  const showPresenceBanner = Boolean(
    attendanceEnabled && presenceStatus.data?.needsConfirm && presenceStatus.data?.canConfirmFromThisIp
  );

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      {showPresenceBanner && (
        <div className="bg-blue-700 px-4 py-2 text-white">
          <div className="flex w-full items-center justify-between gap-3 px-4 text-sm sm:px-5 lg:px-6">
            <span>Hai un ingresso QR da confermare dal PC dello Studio.</span>
            <Button size="sm" variant="secondary" onClick={handleConfirmPresence}>
              <CheckCircle2 className="mr-2 h-4 w-4" />
              Conferma presenza
            </Button>
          </div>
        </div>
      )}
      <div className="w-full px-4 sm:px-5 lg:px-6">
        <div className="flex h-16 justify-between items-center">
          {/* Search */}
          <div className="flex flex-1 items-center justify-center px-2 lg:ml-6 lg:justify-start">
            <div className="w-full max-w-lg lg:max-w-xs">
              <label htmlFor="search" className="sr-only">
                Cerca
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <Search className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <Input
                  id="search"
                  name="search"
                  className="block w-full rounded-md border-0 bg-white py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  placeholder="Cerca clienti, pratiche..."
                  type="search"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-x-4 lg:gap-x-6">
            {/* Messaggi interni */}
            <Popover
              open={staffOpen}
              onOpenChange={(v) => {
                setStaffOpen(v);
              }}
            >
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="relative rounded-full bg-white p-1 text-gray-500 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Messaggi interni</span>
                  <MessagesSquare className="h-6 w-6" aria-hidden="true" />
                  {staffUnread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full bg-blue-600 text-[10px] text-white flex items-center justify-center px-0.5">
                      {staffUnread > 99 ? '99+' : staffUnread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold text-gray-900">Messaggi interni</h3>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {staffPreview.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      Nessun messaggio non letto
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {staffPreview.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            className="w-full px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              setStaffOpen(false);
                              navigate(`/messaggi?conversationId=${encodeURIComponent(c.id)}`);
                            }}
                          >
                            <p className="text-sm font-medium text-gray-900 truncate">{c.title}</p>
                            {c.lastMessage && (
                              <p className="text-xs text-gray-500 truncate">{c.lastMessage.bodyPreview}</p>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="border-t px-4 py-2">
                  <button
                    type="button"
                    className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 py-1"
                    onClick={() => {
                      setStaffOpen(false);
                      navigate('/messaggi');
                    }}
                  >
                    Apri Messaggi
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {/* Appointments bell */}
            <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) refetch(); }}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="relative rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                >
                  <span className="absolute -inset-1.5" />
                  <span className="sr-only">Appuntamenti di oggi</span>
                  <Bell className="h-6 w-6" aria-hidden="true" />
                  {activeCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 rounded-full bg-red-500 text-[10px] text-white flex items-center justify-center px-0.5">
                      {activeCount}
                    </span>
                  )}
                </button>
              </PopoverTrigger>

              <PopoverContent align="end" className="w-80 p-0">
                <div className="px-4 py-3 border-b">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Appuntamenti di oggi
                  </h3>
                </div>

                <div className="max-h-72 overflow-y-auto">
                  {appointments.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      <CalendarCheck className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                      Nessun appuntamento oggi
                    </div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {appointments.map((appt) => (
                        <li key={appt.id}>
                          <button
                            type="button"
                            className="w-full flex items-start gap-3 px-4 py-2.5 text-left hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              setOpen(false);
                              navigate(`/agenda?appointmentId=${appt.id}`);
                            }}
                          >
                            <span
                              className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${statusColor(appt.status)}`}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {appt.title}
                              </p>
                              <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                <Clock className="h-3 w-3" />
                                {formatTime(appt.startTime)} – {formatTime(appt.endTime)}
                              </p>
                            </div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <div className="border-t px-4 py-2">
                  <button
                    type="button"
                    className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-700 py-1"
                    onClick={() => {
                      setOpen(false);
                      navigate('/agenda');
                    }}
                  >
                    Vai all'Agenda
                  </button>
                </div>
              </PopoverContent>
            </Popover>

            {showMobileScanner && (
              <Button variant="outline" size="sm" onClick={() => navigate('/timbra')}>
                <QrCode className="mr-1 h-4 w-4" />
                <span className="hidden sm:inline">Scanner QR</span>
              </Button>
            )}

            {/* Profile / timbratura */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="flex items-center gap-x-2">
                  <User className="h-5 w-5" />
                  <span className="hidden lg:flex lg:items-center">
                    <span className="ml-2 text-sm font-semibold leading-6 text-gray-900">Profilo</span>
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 p-2">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onClick={() => navigate('/profile')}
                >
                  <User className="h-4 w-4" />
                  Profilo
                </button>
                {attendanceEnabled && (
                  <>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={() => setQuickOpen(true)}
                    >
                      <QrCode className="h-4 w-4" />
                      Timbratura rapida
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
                      onClick={() => navigate('/timbratura/me')}
                    >
                      <CalendarCheck className="h-4 w-4" />
                      La mia timbratura
                    </button>
                  </>
                )}
              </PopoverContent>
            </Popover>

            {/* Logout */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex items-center gap-x-2 text-gray-500 hover:text-gray-700"
            >
              <LogOut className="h-5 w-5" />
              <span className="hidden lg:block">Esci</span>
            </Button>
          </div>
        </div>
      </div>
      <Dialog open={quickOpen} onOpenChange={(value) => { setQuickOpen(value); if (!value) setSelectedPreset(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Timbratura rapida da PC</DialogTitle>
          </DialogHeader>
          {!selectedPreset ? (
            <div className="grid gap-3 sm:grid-cols-2">
              {attendancePresets.map((preset) => (
                <Button key={preset.id} variant="outline" className="h-14 justify-start" onClick={() => openQuickPreset(preset)}>
                  {preset.label}
                </Button>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">
                Azione selezionata: <strong>{selectedPreset.label}</strong>
              </div>
              <label className="block text-sm font-medium text-gray-700">
                Motivo
                <select
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                  value={reason}
                  onChange={(event) => setReason(event.target.value as AttendanceEventReason)}
                >
                  <option value="QR_UNAVAILABLE">QR non disponibile</option>
                  <option value="DEVICE_ISSUE">Problema dispositivo</option>
                  <option value="FORGOT_TO_STAMP">Dimenticato di timbrare</option>
                  <option value="EMERGENCY">Emergenza</option>
                  <option value="FIELD_WORK">Lavoro fuori sede</option>
                  <option value="OTHER">Altro</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Nota
                <Textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Descrivi il motivo della timbratura manuale" />
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setSelectedPreset(null)}>Indietro</Button>
                <Button onClick={submitQuickStamp} disabled={submitting}>
                  Registra timbratura
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </header>
  );
}
